import { useEffect, useState, useMemo } from "react";
import { Search } from "lucide-react";
import ConsoleLayout from "@/components/equilibrium/ConsoleLayout";
import { fetchEqProvenance } from "@/lib/eq";

const SEVERITIES = ["all", "informational", "watch", "intervene"];

export default function ConsoleProvenance() {
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState("");
  const [sev, setSev] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEqProvenance(500)
      .then(setEvents)
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return events.filter((e) => {
      if (sev !== "all" && e.severity !== sev) return false;
      if (!q.trim()) return true;
      const s = q.toLowerCase();
      return (
        e.source_module.toLowerCase().includes(s) ||
        e.signal_type.toLowerCase().includes(s) ||
        e.description.toLowerCase().includes(s) ||
        (e.correlation_id || "").toLowerCase().includes(s)
      );
    });
  }, [events, q, sev]);

  return (
    <ConsoleLayout>
      <div data-testid="eq-provenance-page">
        <div className="num-label">PROVENANCE / APPEND-ONLY LEDGER</div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl text-axiom-text font-bold tracking-tight">
          Operational provenance.
        </h1>
        <p className="mt-2 text-axiom-muted text-sm max-w-2xl">
          Every assertion produced by Equilibrium-AI is bound to a tamper-evident event. The
          ledger below is the audit trail behind every dashboard number.
        </p>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
          <div className="flex flex-wrap items-center gap-1 border border-white/10 divide-x divide-white/10">
            {SEVERITIES.map((s) => (
              <button
                key={s}
                data-testid={`eq-prov-sev-${s}`}
                onClick={() => setSev(s)}
                className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider ${
                  sev === s
                    ? "bg-axiom-accent text-axiom-bg"
                    : "bg-axiom-surface text-axiom-muted hover:text-axiom-text"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-axiom-dim" />
            <input
              data-testid="eq-prov-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="search module / signal / description..."
              className="bg-axiom-surface border border-white/10 pl-9 pr-4 py-2 text-sm text-axiom-text placeholder:text-axiom-dim focus:border-axiom-accent focus:outline-none w-full sm:w-80"
            />
          </div>
        </div>

        <div className="mt-6 border border-white/10 bg-axiom-bg overflow-x-auto">
          <table className="w-full text-xs" data-testid="eq-prov-table">
            <thead>
              <tr className="text-left bg-axiom-surface/40 border-b border-white/10 font-mono uppercase tracking-wider text-axiom-dim">
                <th className="px-4 py-3">Timestamp</th>
                <th className="px-4 py-3">Module</th>
                <th className="px-4 py-3">Signal</th>
                <th className="px-4 py-3">Entropy Δ</th>
                <th className="px-4 py-3">Coh%</th>
                <th className="px-4 py-3">Severity</th>
                <th className="px-4 py-3 hidden md:table-cell">Actor</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3 hidden lg:table-cell">Corr</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => {
                const sevColor =
                  e.severity === "intervene"
                    ? "text-amber-300 border-amber-400/40"
                    : e.severity === "watch"
                    ? "text-axiom-accent border-axiom-accent/40"
                    : "text-axiom-muted border-white/15";
                return (
                  <tr
                    key={e.id}
                    data-testid={`eq-prov-row-${e.id}`}
                    className="border-b border-white/5 hover:bg-white/[0.02]"
                  >
                    <td className="px-4 py-3 text-axiom-dim font-mono">
                      {new Date(e.timestamp).toLocaleString(undefined, {
                        month: "short",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="px-4 py-3 font-mono uppercase text-axiom-text">
                      {e.source_module}
                    </td>
                    <td className="px-4 py-3 font-mono text-axiom-muted">{e.signal_type}</td>
                    <td className="px-4 py-3 font-mono text-axiom-text">Δ{e.entropy_delta}</td>
                    <td className="px-4 py-3 font-mono text-axiom-muted">{e.coherence_pct}%</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] border px-2 py-0.5 font-mono uppercase ${sevColor}`}>
                        {e.severity}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-axiom-dim hidden md:table-cell">
                      {e.actor}
                    </td>
                    <td className="px-4 py-3 text-axiom-muted">{e.description}</td>
                    <td className="px-4 py-3 font-mono text-axiom-dim hidden lg:table-cell">
                      {e.correlation_id || "—"}
                    </td>
                  </tr>
                );
              })}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="p-6 text-center text-axiom-muted">
                    No events match the current filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <p className="mt-4 num-label">
          {filtered.length} events · ledger is append-only · provenance.v0.42
        </p>
      </div>
    </ConsoleLayout>
  );
}
